import * as React from "react";

import { cn } from "@/lib/utils";

const cardBaseClasses = "glass-panel rounded-[var(--card-radius)] text-emphasis-primary shadow-card hover:shadow-soft-md hover:-translate-y-0.5 transition-all duration-200 ease-out";
const cardGlassClasses = "liquid-glass rounded-[var(--card-radius)] text-emphasis-primary shadow-soft hover:shadow-soft-md hover:-translate-y-0.5 transition-all duration-200 ease-out";

const Card = React.memo(
  React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn(cardBaseClasses, className)} {...props} />
  )),
);
Card.displayName = "Card";

interface InteractiveCardProps extends React.HTMLAttributes<HTMLElement> {
  href?: string;
}

const InteractiveCard = React.memo(
  React.forwardRef<HTMLDivElement, InteractiveCardProps>(
    ({ className, href, onClick, onKeyDown, ...props }, ref) => {
      const isInteractive = Boolean(onClick || href);
      const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(event as React.KeyboardEvent<HTMLElement>);
        if (event.defaultPrevented || !isInteractive) {
          return;
        }
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          (event.currentTarget as HTMLElement).click();
        }
      };

      if (href) {
        return (
          // eslint-disable-next-line jsx-a11y/anchor-has-content -- content arrives through the spread props (children)
          <a
            ref={ref as unknown as React.Ref<HTMLAnchorElement>}
            href={href}
            className={cn(
              cardBaseClasses,
              "cursor-pointer hover:shadow-card-hover hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-[var(--focus-ring-width)] focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:shadow-card",
              className,
            )}
            onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}
            onKeyDown={handleKeyDown as unknown as React.KeyboardEventHandler<HTMLAnchorElement>}
            {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          />
        );
      }

      return (
        <div
          ref={ref}
          className={cn(
            cardBaseClasses,
            onClick &&
              "cursor-pointer hover:shadow-card-hover hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-[var(--focus-ring-width)] focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:shadow-card",
            className,
          )}
          onClick={onClick}
          onKeyDown={handleKeyDown}
          role={onClick ? "button" : undefined}
          tabIndex={onClick ? 0 : undefined}
          {...props}
        />
      );
    },
  ),
);
InteractiveCard.displayName = "InteractiveCard";

const GlassCard = React.memo(
  React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn(cardGlassClasses, className)} {...props} />
  )),
);
GlassCard.displayName = "GlassCard";

const CardHeader = React.memo(
  React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-sm p-card", className)} {...props} />
  )),
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.memo(
  React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(({ className, ...props }, ref) => (
    // eslint-disable-next-line jsx-a11y/heading-has-content -- content arrives through the spread props (children)
    <h3
      ref={ref}
      className={cn("text-section-title font-semibold tracking-tight text-emphasis-primary", className)}
      {...props}
    />
  )),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.memo(
  React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-body text-emphasis-secondary", className)} {...props} />
  )),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.memo(
  React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-card pt-0", className)} {...props} />
  )),
);
CardContent.displayName = "CardContent";

const CardFooter = React.memo(
  React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-card pt-0", className)} {...props} />
  )),
);
CardFooter.displayName = "CardFooter";

export { Card, GlassCard, InteractiveCard, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
